import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArcButton } from "@/components/ArcButton";
import { ArrowLeft, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const EMPTY = {
  slug: "", encyclopedia: "ghoulax", title: "", subtitle: "", label: "", result_footer: "",
  published: true, questions: [], levels: [],
};

export default function QuizEditor() {
  const { slug } = useParams();
  const isNew = !slug || slug === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [slugTouched, setSlugTouched] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    api.get(`/quizzes/${slug}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => toast.error("Quiz not found")).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isNew]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTitle = (v) => setForm((f) => ({ ...f, title: v, slug: (isNew && !slugTouched) ? slugify(v) : f.slug }));

  // Questions
  const addQuestion = () => setForm((f) => ({ ...f, questions: [...f.questions, { id: uid(), q: "", options: [{ id: uid(), t: "", p: 0 }, { id: uid(), t: "", p: 1 }] }] }));
  const removeQuestion = (qi) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== qi) }));
  const setQuestion = (qi, v) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, q: v } : q) }));
  const addOption = (qi) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, options: [...q.options, { id: uid(), t: "", p: 0 }] } : q) }));
  const removeOption = (qi, oi) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, options: q.options.filter((_, j) => j !== oi) } : q) }));
  const setOption = (qi, oi, k, v) => setForm((f) => ({ ...f, questions: f.questions.map((q, i) => i === qi ? { ...q, options: q.options.map((o, j) => j === oi ? { ...o, [k]: v } : o) } : q) }));

  // Levels
  const addLevel = () => setForm((f) => {
    const nextLevel = (f.levels.reduce((m, l) => Math.max(m, l.level), 0)) + 1;
    return { ...f, levels: [...f.levels, { id: uid(), min: 0, level: nextLevel, name: "", desc: "" }] };
  });
  const removeLevel = (li) => setForm((f) => ({ ...f, levels: f.levels.filter((_, i) => i !== li) }));
  const setLevel = (li, k, v) => setForm((f) => ({ ...f, levels: f.levels.map((l, i) => i === li ? { ...l, [k]: v } : l) }));

  const save = async () => {
    const payload = {
      ...form,
      slug: slugify(form.slug || form.title),
      questions: form.questions.map((q) => ({ ...q, options: q.options.map((o) => ({ ...o, p: Number(o.p) || 0 })) })),
      levels: form.levels.map((l) => ({ ...l, min: Number(l.min) || 0, level: Number(l.level) || 1 })),
    };
    if (!payload.title.trim()) { toast.error("Title is required"); return; }
    if (!payload.slug) { toast.error("Slug is required"); return; }
    if (payload.levels.length === 0) { toast.error("Add at least one result level"); return; }
    try {
      if (isNew) await api.post("/quizzes", payload);
      else await api.put(`/quizzes/${slug}`, payload);
      toast.success("Quiz saved");
      navigate("/admin/quizzes");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="mx-auto max-w-3xl px-4 py-16 text-center text-sm" style={{ color: "var(--arc-text-2)" }}>Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <Link to="/admin/quizzes" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}><ArrowLeft size={14} /> Quizzes</Link>
      <h1 className="mt-4 font-serif-arc text-3xl font-black">{isNew ? "New Quiz" : "Edit Quiz"}</h1>

      {/* Meta */}
      <div className="mt-8 space-y-4 rounded-md border p-5 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
        <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => onTitle(e.target.value)} data-testid="quiz-title-input" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Slug (URL)</Label>
            <Input value={form.slug} onChange={(e) => { setSlugTouched(true); setField("slug", e.target.value); }} disabled={!isNew} data-testid="quiz-slug-input" style={{ backgroundColor: "var(--arc-bg)" }} />
            {!isNew && <p className="text-xs" style={{ color: "var(--arc-text-2)" }}>Editable on the shared /quiz/{form.slug} link.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Encyclopedia</Label>
            <Select value={form.encyclopedia} onValueChange={(v) => setField("encyclopedia", v)}>
              <SelectTrigger data-testid="quiz-enc-select" style={{ backgroundColor: "var(--arc-bg)" }}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ghoulax">Ghoulax</SelectItem><SelectItem value="zyfley">Zyfley</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5"><Label>Subtitle / tagline</Label><Input value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Badge label (optional)</Label><Input value={form.label || ""} onChange={(e) => setField("label", e.target.value)} placeholder="e.g. SATIRE" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="flex items-center gap-2 pt-6"><Switch checked={form.published} onCheckedChange={(v) => setField("published", v)} data-testid="quiz-published" /><Label>Published (visible to visitors)</Label></div>
        </div>
        <div className="space-y-1.5"><Label>Result footer note (optional)</Label><Input value={form.result_footer || ""} onChange={(e) => setField("result_footer", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
      </div>

      {/* Questions */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-serif-arc text-xl font-bold">Questions</h2>
          <ArcButton variant="outline" onClick={addQuestion} data-testid="add-question"><Plus size={15} /> Add question</ArcButton>
        </div>
        <div className="space-y-4">
          {form.questions.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No questions yet. Add one to get started.</p>}
          {form.questions.map((q, qi) => (
            <div key={q.id} data-testid={`question-${qi}`} className="rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
              <div className="flex items-start gap-2">
                <span className="mt-2 text-xs font-bold" style={{ color: "var(--arc-text-2)" }}>Q{qi + 1}</span>
                <Textarea rows={2} value={q.q} onChange={(e) => setQuestion(qi, e.target.value)} placeholder="Question text" className="flex-1" style={{ backgroundColor: "var(--arc-bg)" }} />
                <button onClick={() => removeQuestion(qi)} className="mt-1 text-red-600" data-testid={`remove-question-${qi}`}><Trash2 size={16} /></button>
              </div>
              <div className="mt-3 space-y-2 border-l pl-3" style={{ borderColor: "var(--arc-border)" }}>
                <div className="grid grid-cols-[1fr_72px_32px] gap-2 text-xs font-semibold" style={{ color: "var(--arc-text-2)" }}>
                  <span>Answer option</span><span>Points</span><span></span>
                </div>
                {q.options.map((o, oi) => (
                  <div key={o.id} className="grid grid-cols-[1fr_72px_32px] items-center gap-2">
                    <Input value={o.t} onChange={(e) => setOption(qi, oi, "t", e.target.value)} placeholder="Answer text" data-testid={`option-${qi}-${oi}`} style={{ backgroundColor: "var(--arc-bg)" }} />
                    <Input type="number" value={o.p} onChange={(e) => setOption(qi, oi, "p", e.target.value)} data-testid={`option-points-${qi}-${oi}`} style={{ backgroundColor: "var(--arc-bg)" }} />
                    <button onClick={() => removeOption(qi, oi)} className="text-red-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={() => addOption(qi)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid={`add-option-${qi}`} style={{ color: "var(--arc-text-2)" }}><Plus size={13} /> Add option</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Levels */}
      <div className="mt-8">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-serif-arc text-xl font-bold">Result Levels</h2>
          <ArcButton variant="outline" onClick={addLevel} data-testid="add-level"><Plus size={15} /> Add level</ArcButton>
        </div>
        <p className="mb-3 text-xs" style={{ color: "var(--arc-text-2)" }}>The visitor's total points map to the highest level whose "min score" they meet.</p>
        <div className="space-y-3">
          {form.levels.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No levels yet. Add at least one.</p>}
          {form.levels.map((l, li) => (
            <div key={l.id} data-testid={`level-${li}`} className="rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="space-y-1"><Label className="text-xs">Level #</Label><Input type="number" value={l.level} onChange={(e) => setLevel(li, "level", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
                <div className="space-y-1"><Label className="text-xs">Min score</Label><Input type="number" value={l.min} onChange={(e) => setLevel(li, "min", e.target.value)} data-testid={`level-min-${li}`} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
                <div className="space-y-1 col-span-2"><Label className="text-xs">Name</Label><Input value={l.name} onChange={(e) => setLevel(li, "name", e.target.value)} data-testid={`level-name-${li}`} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
              </div>
              <div className="mt-3 flex items-end gap-2">
                <div className="flex-1 space-y-1"><Label className="text-xs">Description</Label><Textarea rows={2} value={l.desc} onChange={(e) => setLevel(li, "desc", e.target.value)} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
                <button onClick={() => removeLevel(li)} className="mb-2 text-red-600" data-testid={`remove-level-${li}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        <ArcButton variant={form.encyclopedia === "zyfley" ? "zyfley" : "ghoulax"} onClick={save} data-testid="save-quiz">{isNew ? "Create Quiz" : "Save Changes"}</ArcButton>
        <ArcButton variant="outline" to="/admin/quizzes">Cancel</ArcButton>
      </div>
    </div>
  );
}
